import { Injectable } from '@angular/core';

import { Observable, filter, map, switchMap } from 'rxjs';

import {
  CamBaseService,
  GraphEndpoint,
  GraphReponsePaged,
  HandleComplexRequest,
  graphAggregatorEndpoint,
} from '@camelot/server';
import { isNonNullable } from '@camelot/utils';

import {
  Reception,
  ReceptionCancellationPayload,
  ReceptionCreationPayload,
  ReceptionModifierPayload,
  ReceptionRowInput,
} from './dto/reception';
import {
  CANCEL_RECEPTION,
  CREATE_RECEPTION,
  LINK_PURCHASE_ORDERS_TO_RECEPTION,
  UNLINK_PURCHASE_ORDER_FROM_RECEPTION,
  UPDATE_RECEPTION,
  UPDATE_RECEPTION_ROWS,
  VALIDATE_RECEPTION,
} from './mutations';
import { GET_RECEPTION } from './queries';

const graphEndpoint: GraphEndpoint = {
  clientName: 'purchaseService',
  endpoint: 'purchase',
};

const CLEAR_CACHE = ['receptions', 'receptionView'];
// Valider ou annuler recalcule l'avancement des commandes liées.
const CLEAR_CACHE_WITH_ORDERS = [...CLEAR_CACHE, 'purchaseOrders', 'purchaseOrderView'];

@Injectable({
  providedIn: 'root',
})
export class CamReceptionsService extends CamBaseService {
  public reception = new HandleComplexRequest<Reception>();

  constructor() {
    super();
    super.registerRoutes({ graphEndpoint });
  }

  public fetchReception$(id: string) {
    return this.reception.fetch(
      id,
      this._graphService
        .fetchQueryBuilder<GraphReponsePaged<Reception>>(GET_RECEPTION(id), graphAggregatorEndpoint.clientName)
        .pipe(
          filter(isNonNullable),
          map(data => data.items?.[0] ?? null),
          filter(isNonNullable)
        )
    );
  }

  public createReception$(payload: ReceptionCreationPayload) {
    return this._graphService.mutateBuilder<string>(CREATE_RECEPTION(payload), graphEndpoint.clientName, CLEAR_CACHE);
  }

  public updateReception$(payload: ReceptionModifierPayload) {
    return this._refetchAfter$(
      this._graphService.mutateBuilder<string>(UPDATE_RECEPTION(payload), graphEndpoint.clientName, CLEAR_CACHE)
    );
  }

  public updateReceptionRows$(receptionId: string, rows: ReceptionRowInput[]) {
    return this._refetchAfter$(
      this._graphService.mutateBuilder<string>(
        UPDATE_RECEPTION_ROWS(receptionId, rows),
        graphEndpoint.clientName,
        CLEAR_CACHE
      )
    );
  }

  public validateReception$(receptionId: string) {
    return this._refetchAfter$(
      this._graphService.mutateBuilder<string>(
        VALIDATE_RECEPTION(receptionId),
        graphEndpoint.clientName,
        CLEAR_CACHE_WITH_ORDERS
      )
    );
  }

  public cancelReception$(payload: ReceptionCancellationPayload) {
    return this._refetchAfter$(
      this._graphService.mutateBuilder<string>(
        CANCEL_RECEPTION(payload),
        graphEndpoint.clientName,
        CLEAR_CACHE_WITH_ORDERS
      )
    );
  }

  public linkPurchaseOrders$(receptionId: string, purchaseOrderIds: string[]) {
    return this._refetchAfter$(
      this._graphService.mutateBuilder<string>(
        LINK_PURCHASE_ORDERS_TO_RECEPTION(receptionId, purchaseOrderIds),
        graphEndpoint.clientName,
        CLEAR_CACHE
      )
    );
  }

  public unlinkPurchaseOrder$(receptionId: string, purchaseOrderId: string) {
    return this._refetchAfter$(
      this._graphService.mutateBuilder<string>(
        UNLINK_PURCHASE_ORDER_FROM_RECEPTION(receptionId, purchaseOrderId),
        graphEndpoint.clientName,
        CLEAR_CACHE
      )
    );
  }

  // Les mutations ne renvoient que l'id : on relit la réception pour mettre le cache à jour.
  private _refetchAfter$(mutation$: Observable<string>) {
    return mutation$.pipe(switchMap(id => this.fetchReception$(id).pipe(map(() => id))));
  }
}
